import { useState, useEffect, useCallback } from 'react';
import { LessonProgress, FlashcardProgress, QuizResult } from '../types';
import {
  getLessonProgress,
  getFlashcardProgress,
  getQuizResults,
  markLessonComplete,
  saveFlashcardResult,
  saveQuizResult,
  getStreak,
  updateDailyStreak,
  logActivity,
} from '../lib/supabase';

interface ProgressState {
  lessons: LessonProgress[];
  flashcards: FlashcardProgress[];
  quizResults: QuizResult[];
  streak: number;
  isLoading: boolean;
}

export function useProgress(userId: string | undefined, courseId: string) {
  const [state, setState] = useState<ProgressState>({
    lessons: [],
    flashcards: [],
    quizResults: [],
    streak: 0,
    isLoading: true,
  });

  const loadProgress = useCallback(async () => {
    if (!userId) {
      setState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      const [lessons, flashcards, quizResults, streak] = await Promise.all([
        getLessonProgress(userId, courseId),
        getFlashcardProgress(userId, courseId),
        getQuizResults(userId, courseId),
        getStreak(userId),
      ]);

      setState({
        lessons,
        flashcards,
        quizResults,
        streak,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error loading progress:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [userId, courseId]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const completeLesson = async (lessonId: string) => {
    if (!userId) return;
    await markLessonComplete(userId, courseId, lessonId);
    await updateDailyStreak(userId, 'lesson', 5);
    loadProgress();
  };

  const recordFlashcardResult = async (flashcardId: string, knew: boolean) => {
    if (!userId) return;
    await saveFlashcardResult(userId, courseId, flashcardId, knew);
  };

  const submitQuizResult = async (
    mode: 'quiz' | 'test' | 'random',
    score: number,
    total: number,
    wrongQuestions: string[],
    timeSeconds: number,
    details?: {
      mc_correct?: number;
      mc_total?: number;
      written_earned?: number;
      written_max?: number;
      all_question_ids?: string[];
    }
  ) => {
    if (!userId) return null;

    const percentage = Math.round((score / total) * 100);

    const result = await saveQuizResult({
      user_id: userId,
      course_id: courseId,
      mode,
      score,
      total,
      percentage,
      wrong_questions: wrongQuestions,
      time_seconds: timeSeconds,
      details: details || {},
    });

    // Log activity for Admin Dashboard
    await logActivity(userId, 'quiz', {
      course_id: courseId,
      mode,
      score,
      total,
      percentage,
      duration_minutes: Math.ceil(timeSeconds / 60),
      ...(details || {}),
    });

    await updateDailyStreak(userId, mode, Math.ceil(timeSeconds / 60));
    loadProgress();

    return result;
  };

  return {
    ...state,
    completeLesson,
    recordFlashcardResult,
    submitQuizResult,
    refresh: loadProgress,
  };
}
